import { useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { AUTO_ASSIGN_STAGING_REASONS } from "@miplatform/constants";
import { toastService } from "@miplatform/toast";
import { IAttemptListItemResponseForWorkOrder } from "@miplatform/types";
import { Box } from "@mui/material";
import { useCalendarContext } from "../../../providers";
import AssignableUserColumn from "../UnassignedWorkOrders/AssignableUserColumn";
import { AssignableUserContainer } from "../UnassignedWorkOrders/AssignableUserContainer";
import { UnassignedWorkOrders } from "../UnassignedWorkOrders/UnassignedWorkOrders";
import { AssignmentViewActions } from "./AssignmentViewActions";
import { CancelAssignmentStagingModal } from "./CancelAssignmentStagingModal";
import { useAssignmentStaging } from "./useAssignmentStaging";
import { useNHCPriorityScheduler } from "./useNHCPriorityScheduler";

const COLUMN_WIDTH = 180;
const HOUR_HEIGHT = 60;

interface AssignmentViewProps {
  scheduler: ReturnType<typeof useNHCPriorityScheduler>;
}

export function AssignmentView({ scheduler }: Readonly<AssignmentViewProps>) {
  const { classes } = useStyles();
  const {
    zoom,
    date,
    users,
    calendarSet,
    events,
    refreshData,
    handleSelectUser,
  } = useCalendarContext();
  const [discardStagingModalOpen, setDiscardStagingModalOpen] = useState(false);

  const {
    activeAssignableUserIds,
    assignableUsersAvailableTime,
    boardExpandedUserId,
    effectiveSelectedUserId,
    expandedUsers,
    getAssignableUsersAvailableTime,
    handleAddAssignableUser,
    handleBoardExpandedChange,
    handleCloseUserColumn,
    handleUserSelect,
    isLoading,
    isWorkOrdersLoading,
    rawUnassignedWorkOrders,
  } = scheduler;

  const {
    stagedByUserId,
    stagedIds,
    confirmedAttemptIds,
    totalStagedCount,
    isConfirming,
    stageWorkOrderForUser,
    stageWorkOrdersForUser,
    undoLastTransfer,
    confirmStaging,
    clearStaging,
    unstageAttempt,
    getStagedForUser,
    getRemainingTemplateDurationForUser,
  } = useAssignmentStaging(calendarSet?.config ?? null, date, events);

  useEffect(() => {
    getAssignableUsersAvailableTime();
  }, [date]);

  const handleWorkOrderClick = (
    attempt: IAttemptListItemResponseForWorkOrder
  ) => {
    const targetUserId = effectiveSelectedUserId ?? activeAssignableUserIds[0];
    if (!targetUserId) {
      return;
    }
    if (!effectiveSelectedUserId && activeAssignableUserIds[0]) {
      handleUserSelect(activeAssignableUserIds[0]);
    }
    const result = stageWorkOrderForUser(attempt, targetUserId);
    if (
      !result.staged &&
      result.reason === AUTO_ASSIGN_STAGING_REASONS.INSUFFICIENT_WINDOW_CAPACITY
    ) {
      toastService.error({
        text: "This work order does not fit in the selected template time window.",
      });
    }
  };

  const handleBulkAssign = (
    attempts: IAttemptListItemResponseForWorkOrder[]
  ) => {
    const targetUserId = effectiveSelectedUserId ?? activeAssignableUserIds[0];
    if (!targetUserId || attempts.length === 0) {
      return;
    }
    if (!effectiveSelectedUserId && activeAssignableUserIds[0]) {
      handleUserSelect(activeAssignableUserIds[0]);
    }
    const result = stageWorkOrdersForUser(attempts, targetUserId);
    if (result.blockedCount > 0) {
      toastService.error({
        text: `${result.blockedCount} work order(s) did not fit in template time windows.`,
      });
    }
  };

  const handleCloseAssignableColumn = (userId: string) => {
    handleCloseUserColumn(userId);
  };

  const handleRequestDiscardStaging = () => {
    if (totalStagedCount > 0) {
      setDiscardStagingModalOpen(true);
    }
  };

  const handleCloseDiscardStagingModal = () => {
    setDiscardStagingModalOpen(false);
  };

  const handleConfirmDiscardStaging = () => {
    clearStaging();
    setDiscardStagingModalOpen(false);
  };

  const handleRemoveStagedAttempt = (userId: string, attemptId: string) => {
    unstageAttempt(userId, attemptId);
  };

  const handleConfirmStaging = async () => {
    const success = await confirmStaging();
    if (success) {
      refreshData();
      getAssignableUsersAvailableTime();
      toastService.success({
        text: "Work orders assigned successfully.",
      });
    }
  };

  const getRemainingDurationForUser = useCallback(
    (userId?: string) => {
      if (!userId) {
        return 0;
      }
      const user = assignableUsersAvailableTime.find(
        (user) => user.id === userId
      );

      if (!user) {
        return 0;
      }

      return (
        user.availableTime * 60 -
        getStagedForUser(userId).reduce(
          (acc, curr) => acc + curr.attempt.expectedDuration,
          0
        )
      );
    },
    [assignableUsersAvailableTime, getStagedForUser]
  );

  const hasVisibleAssignableColumns = activeAssignableUserIds.length > 0;
  const actionsDisabled = totalStagedCount === 0;
  const selectedUserId = effectiveSelectedUserId ?? activeAssignableUserIds[0];

  return (
    <Box className={classes.container}>
      <Box className={classes.assignmentPanel}>
        <AssignableUserContainer
          assignableUsersAvailableTime={assignableUsersAvailableTime}
          isLoading={isLoading}
          onUserSelect={handleUserSelect}
          selectedUserId={effectiveSelectedUserId}
          activeAssignableUserIds={activeAssignableUserIds}
          stagedByUserId={stagedByUserId}
          onAddAssignableUser={handleAddAssignableUser}
        />
        <UnassignedWorkOrders
          rawUnassignedWorkOrders={rawUnassignedWorkOrders}
          isWorkOrdersLoading={isWorkOrdersLoading}
          stagedAttemptIds={stagedIds}
          confirmedAttemptIds={confirmedAttemptIds}
          hasAssignableUserColumn={hasVisibleAssignableColumns}
          remainingDurationForUser={getRemainingDurationForUser(selectedUserId)}
          remainingDurationByTemplateId={
            selectedUserId
              ? getRemainingTemplateDurationForUser(selectedUserId)
              : undefined
          }
          onWorkOrderClick={handleWorkOrderClick}
          onBulkAssign={handleBulkAssign}
        />
        {hasVisibleAssignableColumns && (
          <AssignmentViewActions
            stagedCount={totalStagedCount}
            disabled={actionsDisabled || isConfirming}
            onCancel={handleRequestDiscardStaging}
            onUndo={undoLastTransfer}
            onConfirm={handleConfirmStaging}
          />
        )}
        <CancelAssignmentStagingModal
          open={discardStagingModalOpen}
          stagedCount={totalStagedCount}
          onClose={handleCloseDiscardStagingModal}
          onConfirmCancel={handleConfirmDiscardStaging}
        />
      </Box>

      {expandedUsers.map((user) => {
        const isBoardExpandedUser =
          boardExpandedUserId !== null && user.id === boardExpandedUserId;
        const currentUser = users.find((u) => u.id === user.id);
        if (boardExpandedUserId !== null && user.id !== boardExpandedUserId) {
          return null;
        }

        return (
          <AssignableUserColumn
            key={user.id}
            user={currentUser || user}
            selectedDate={date}
            selected={user.id === effectiveSelectedUserId}
            columnWidth={COLUMN_WIDTH}
            hourHeight={HOUR_HEIGHT}
            zoom={zoom}
            stagedAssignments={getStagedForUser(user.id)}
            assignedEvents={events.filter(
              (event) => event.assignedUserId === user.id
            )}
            onRemoveStagedAttempt={(attemptId) =>
              handleRemoveStagedAttempt(user.id, attemptId)
            }
            onClose={() => handleCloseAssignableColumn(user.id)}
            boardExpanded={isBoardExpandedUser}
            onBoardExpandedChange={(expanded: boolean) => {
              handleBoardExpandedChange(user.id, expanded);
              handleSelectUser(expanded ? user : null);
            }}
          />
        );
      })}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: theme.spacing(1),
  },
  assignmentPanel: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    overflow: "auto",
    flex: 1,
  },
}));
